import { useEffect, useMemo } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import {
    AXIS_SPHERE_LAYOUT,
    BOTTOM_NAV_LAYOUT,
    JOYSTICKS_LAYOUT,
    MINIMAP_LAYOUT,
    OFFLINE_INDICATOR_LAYOUT,
    PROCORE_BOTTOM_ENTRY,
    PROCORE_TOP_BAR,
    TOOLBAR_LAYOUT,
} from "components/toolbars/constants";
import { useDeviceOrientation, useIsPhone } from "contexts/panelMode";
import { VISIBILITY_TOOLBAR_IDS } from "contexts/toolbars";
import { selectCurrentDrawing } from "features/miniMap/minimapSlice";
import {
    selectIsAxisSphereVisible,
    selectIsJoysticksVisible,
    selectIsMiniMapVisible,
    selectIsOnline,
    selectIsProcoreMobileDesign,
    selectSceneDbBackfill,
} from "slices/explorer";
import { selectIsFocusedToolMode } from "slices/procoreUx";
import { selectIsToolbarVisible } from "slices/toolbarVisibility";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** A rectangular area cut out of one viewport corner (px from the corner edge). */
export interface ViewportSafeAreaCorner {
    width: number;
    height: number;
}

/**
 * Per-edge inset (CSS px) plus optional corner cutouts describing the region
 * of the viewport occupied by fixed UI chrome.
 *
 * Any overlay (gizmo, HUD element) should stay **inside** the safe rectangle
 * to avoid being hidden behind standard controls.
 *
 * Edge insets apply across the full edge length. Corner cutouts are additive
 * rectangular exclusions measured from each respective corner of the viewport.
 */
export interface ViewportSafeArea {
    top: number;
    right: number;
    bottom: number;
    left: number;
    topLeft?: ViewportSafeAreaCorner;
    topRight?: ViewportSafeAreaCorner;
    bottomLeft?: ViewportSafeAreaCorner;
    bottomRight?: ViewportSafeAreaCorner;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const SAFE_AREA_PADDING = 12;

const DESKTOP_TOOLBAR_MARGIN = 24;

/**
 * Extra clearance (px) baked into corner cutouts so a
 * clamped overlay keeps its full body.
 */
const EXTRA_CLEARANCE = 56;

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the viewport safe area: rectangular insets plus corner cutouts that
 * account for all visible fixed UI controls.
 *
 * Insets already include {@link SAFE_AREA_PADDING} breathing room so gizmos
 * placed at the boundary won't touch chrome or the raw screen edge.
 *
 * **Procore desktop:**
 *   • Top inset: offline-indicator height when visible, otherwise default margin
 *   • Bottom inset: bottom nav toolbar reservation when visible, otherwise default margin
 *   • Top-right corner: navigation sphere when visible
 *   • Bottom-right corner: minimap when visible
 *
 * **Procore mobile:**
 *   • Global top inset (top bar)
 *   • Top-left corner (minimap, when visible)
 *   • Top-right corner (navigation sphere, when visible)
 *   • Bottom-left / bottom-right corners (joysticks, when visible)
 *
 * @param padding Extra breathing room (px) added to every inset. Defaults to {@link SAFE_AREA_PADDING}.
 * @param debug When true, draws a live debug overlay (red dashed rectangle + corner fills) via a
 *   fixed-position SVG injected into `document.body`. Development use only.
 */
export function useViewportSafeArea(padding: number = SAFE_AREA_PADDING, debug = false): ViewportSafeArea {
    const isProcoreMobile = useAppSelector(selectIsProcoreMobileDesign);
    const isPhone = useIsPhone();
    const orientation = useDeviceOrientation();
    const isNavWheelVisible = useAppSelector((s) => selectIsToolbarVisible(s, VISIBILITY_TOOLBAR_IDS.NAV_WHEEL));
    const isOnline = useAppSelector(selectIsOnline);
    const backfill = useAppSelector(selectSceneDbBackfill);
    // The desktop top-center chip: the offline toast, or the scene DB backfill toast.
    const isTopHintVisible = !isOnline || backfill.preparing || Boolean(backfill.ready);
    const isFocusedToolMode = useAppSelector(selectIsFocusedToolMode);
    // Mobile hides these three overlays in focused tool modes (see HudProcoreMobile),
    // so the corners they normally occupy are free for gizmos to use. Desktop keeps
    // them on screen in those modes, so it keeps reserving the space.
    const overlaysHiddenForToolMode = isProcoreMobile && isFocusedToolMode;
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible) && !overlaysHiddenForToolMode;
    const isJoysticksVisible = useAppSelector(selectIsJoysticksVisible) && !overlaysHiddenForToolMode;
    const isMiniMapVisible = useAppSelector(selectIsMiniMapVisible) && !overlaysHiddenForToolMode;
    const isMiniMapShown = useAppSelector(selectCurrentDrawing) != null && isMiniMapVisible;

    const safeArea = useMemo<ViewportSafeArea>(() => {
        if (isProcoreMobile) {
            return computeMobileSafeArea(
                isPhone,
                orientation,
                isAxisSphereVisible,
                isJoysticksVisible,
                isMiniMapShown,
                padding,
            );
        }
        return computeDesktopSafeArea(
            isAxisSphereVisible,
            isNavWheelVisible,
            isMiniMapShown,
            isTopHintVisible,
            padding,
        );
    }, [
        isProcoreMobile,
        isPhone,
        orientation,
        isAxisSphereVisible,
        isJoysticksVisible,
        isMiniMapShown,
        isNavWheelVisible,
        isTopHintVisible,
        padding,
    ]);

    useEffect(() => {
        if (!debug) {
            return;
        }

        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("data-safe-area-debug", "");
        Object.assign(svg.style, {
            position: "fixed",
            inset: "0",
            pointerEvents: "none",
            zIndex: "9999",
            overflow: "visible",
        });
        document.body.appendChild(svg);

        function draw() {
            // The SVG is fixed over the whole window, but the safe area (and clampToSafeArea)
            // are relative to the render CANVAS, which may be inset/offset by surrounding UI
            // chrome. Draw against the canvas's bounding rect so the overlay is strictly exact
            // - using window dimensions would overshoot whenever the canvas isn't full-window.
            const canvas = document.getElementById("main-canvas");
            const rect = canvas?.getBoundingClientRect();
            const ox = rect?.left ?? 0;
            const oy = rect?.top ?? 0;
            const cw = rect?.width ?? window.innerWidth;
            const ch = rect?.height ?? window.innerHeight;

            svg.setAttribute("width", String(window.innerWidth));
            svg.setAttribute("height", String(window.innerHeight));
            while (svg.firstChild) {
                svg.removeChild(svg.firstChild);
            }

            const sa = safeArea;

            const addRect = (
                x: number,
                y: number,
                rw: number,
                rh: number,
                fill: string,
                stroke: string,
                dash?: string,
            ) => {
                const r = document.createElementNS("http://www.w3.org/2000/svg", "rect");
                // Coordinates are canvas-relative; offset into window space for the fixed SVG.
                r.setAttribute("x", String(ox + x));
                r.setAttribute("y", String(oy + y));
                r.setAttribute("width", String(rw));
                r.setAttribute("height", String(rh));
                r.setAttribute("fill", fill);
                r.setAttribute("stroke", stroke);
                r.setAttribute("stroke-width", "1");
                if (dash) {
                    r.setAttribute("stroke-dasharray", dash);
                }
                svg.appendChild(r);
            };

            addRect(sa.left, sa.top, cw - sa.left - sa.right, ch - sa.top - sa.bottom, "none", "rgb(255,50,50)", "6 3");
            if (sa.topLeft) {
                addRect(0, 0, sa.topLeft.width, sa.topLeft.height, "rgba(255,50,50,0.1)", "rgba(255,50,50,0.5)");
            }
            if (sa.topRight) {
                addRect(
                    cw - sa.topRight.width,
                    0,
                    sa.topRight.width,
                    sa.topRight.height,
                    "rgba(255,50,50,0.1)",
                    "rgba(255,50,50,0.5)",
                );
            }
            if (sa.bottomLeft) {
                addRect(
                    0,
                    ch - sa.bottomLeft.height,
                    sa.bottomLeft.width,
                    sa.bottomLeft.height,
                    "rgba(255,50,50,0.1)",
                    "rgba(255,50,50,0.5)",
                );
            }
            if (sa.bottomRight) {
                addRect(
                    cw - sa.bottomRight.width,
                    ch - sa.bottomRight.height,
                    sa.bottomRight.width,
                    sa.bottomRight.height,
                    "rgba(255,50,50,0.1)",
                    "rgba(255,50,50,0.5)",
                );
            }
        }

        draw();
        const observer = new ResizeObserver(draw);
        observer.observe(document.documentElement);
        const canvasEl = document.getElementById("main-canvas");
        if (canvasEl) {
            observer.observe(canvasEl);
        }

        return () => {
            svg.remove();
            observer.disconnect();
        };
    }, [debug, safeArea]);

    return safeArea;
}

// ─────────────────────────────────────────────────────────────────────────────
// Point clamping helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Clamps a 2-D screen point so it stays within the safe area rectangle and
 * outside any corner cutouts. Returns the clamped [x, y].
 */
export function clampToSafeArea(
    x: number,
    y: number,
    canvasW: number,
    canvasH: number,
    sa: ViewportSafeArea,
): [number, number] {
    // Edge clamp
    let cx = Math.max(sa.left, Math.min(canvasW - sa.right, x));
    let cy = Math.max(sa.top, Math.min(canvasH - sa.bottom, y));

    // Corner cutouts - push the point to the nearest clear edge of the cutout
    if (sa.topLeft && cx < sa.topLeft.width && cy < sa.topLeft.height) {
        const ratioX = cx / sa.topLeft.width;
        const ratioY = cy / sa.topLeft.height;
        if (ratioX < ratioY) {
            cx = sa.topLeft.width;
        } else {
            cy = sa.topLeft.height;
        }
    }
    if (sa.topRight && cx > canvasW - sa.topRight.width && cy < sa.topRight.height) {
        const ratioX = (canvasW - cx) / sa.topRight.width;
        const ratioY = cy / sa.topRight.height;
        if (ratioX < ratioY) {
            cx = canvasW - sa.topRight.width;
        } else {
            cy = sa.topRight.height;
        }
    }
    if (sa.bottomLeft && cx < sa.bottomLeft.width && cy > canvasH - sa.bottomLeft.height) {
        const ratioX = cx / sa.bottomLeft.width;
        const ratioY = (canvasH - cy) / sa.bottomLeft.height;
        if (ratioX < ratioY) {
            cx = sa.bottomLeft.width;
        } else {
            cy = canvasH - sa.bottomLeft.height;
        }
    }
    if (sa.bottomRight && cx > canvasW - sa.bottomRight.width && cy > canvasH - sa.bottomRight.height) {
        const ratioX = (canvasW - cx) / sa.bottomRight.width;
        const ratioY = (canvasH - cy) / sa.bottomRight.height;
        if (ratioX < ratioY) {
            cx = canvasW - sa.bottomRight.width;
        } else {
            cy = canvasH - sa.bottomRight.height;
        }
    }

    return [cx, cy];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function computeDesktopSafeArea(
    isAxisSphereVisible: boolean,
    isNavWheelVisible: boolean,
    isMiniMapVisible: boolean,
    isHintVisible: boolean,
    padding: number,
): ViewportSafeArea {
    const p = padding;

    // Top: reserve extra space when the offline-indicator hint is visible.
    const top = (isHintVisible ? OFFLINE_INDICATOR_LAYOUT.HEIGHT : DESKTOP_TOOLBAR_MARGIN) + 28 + p;

    // Bottom: toolbar pill sits at bottom: BOTTOM_OFFSET and reserves RESERVED_HEIGHT px above it.
    const bottom = (isNavWheelVisible ? BOTTOM_NAV_LAYOUT.RESERVED_HEIGHT : DESKTOP_TOOLBAR_MARGIN) + p;

    // Top-right: navigation sphere at right:16 top:16 (HudProcore; only when visible).
    const topRight = isAxisSphereVisible
        ? {
              width: AXIS_SPHERE_LAYOUT.DESKTOP_MARGIN + AXIS_SPHERE_LAYOUT.DESKTOP_SIZE + 48 + p,
              height: AXIS_SPHERE_LAYOUT.DESKTOP_MARGIN + AXIS_SPHERE_LAYOUT.DESKTOP_SIZE + 32 + p,
          }
        : undefined;

    // Bottom-right: minimap at right:WEB_MARGIN bottom:WEB_MARGIN (miniMapWeb.tsx; only when visible).
    const bottomRight = isMiniMapVisible
        ? {
              width: MINIMAP_LAYOUT.WEB_MARGIN + MINIMAP_LAYOUT.WEB_WIDTH + EXTRA_CLEARANCE + p,
              height: MINIMAP_LAYOUT.WEB_MARGIN + MINIMAP_LAYOUT.WEB_HEIGHT + EXTRA_CLEARANCE + p,
          }
        : undefined;

    return {
        top,
        right: DESKTOP_TOOLBAR_MARGIN + 48 + p,
        bottom,
        left: DESKTOP_TOOLBAR_MARGIN + 36 + p,
        topRight,
        bottomRight,
    };
}

function computeMobileSafeArea(
    isPhone: boolean,
    orientation: "portrait" | "landscape",
    isAxisSphereVisible: boolean,
    isJoysticksVisible: boolean,
    isMiniMapVisible: boolean,
    padding: number,
): ViewportSafeArea {
    const margin = isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET;
    const p = padding;

    // ── Global top - full-width top bar ───────────────────────────────────
    const topBarHeight =
        isPhone && orientation === "portrait" ? PROCORE_TOP_BAR.HEIGHT_PORTRAIT : PROCORE_TOP_BAR.HEIGHT;
    const top = topBarHeight + p;

    // ── Global bottom - full-width bottom toolbar ─────────────────────────
    let bottom = margin + PROCORE_BOTTOM_ENTRY.TOOLBAR_HEIGHT + p;

    // ── Global left / right - minimal edge breathing room ─────────────────
    const left = margin + p;
    const right = margin + p;

    // ── Top-right corner - navigation sphere ──────────────────────────────
    // Sphere is at: right=margin, top=topBarHeight+margin
    // Corner height measured from viewport top (includes top bar area + sphere).
    let topRight: ViewportSafeAreaCorner | undefined;
    if (isAxisSphereVisible) {
        const sphereSize = isPhone ? AXIS_SPHERE_LAYOUT.PHONE_SIZE : AXIS_SPHERE_LAYOUT.TABLET_SIZE;
        topRight = {
            width: margin + sphereSize + 24 + p,
            height: topBarHeight + margin + sphereSize + 24 + p,
        };
    }

    // ── Top-left corner - minimap ─────────────────────────────────────────
    // Minimap sits below the top bar at left=margin (miniMapMobile.tsx; only when visible).
    // Height measured from viewport top (includes top bar area + minimap).
    let topLeft: ViewportSafeAreaCorner | undefined;
    if (isMiniMapVisible) {
        topLeft = {
            width: margin + MINIMAP_LAYOUT.MOBILE_WIDTH + EXTRA_CLEARANCE + p,
            height: topBarHeight + margin + MINIMAP_LAYOUT.MOBILE_HEIGHT + EXTRA_CLEARANCE + p,
        };
    }

    // ── Bottom corners - joysticks ─────────────────────────────────────────
    // Joysticks sit above the bottom toolbar.  Heights measured from viewport bottom.
    let bottomLeft: ViewportSafeAreaCorner | undefined;
    let bottomRight: ViewportSafeAreaCorner | undefined;
    if (isJoysticksVisible) {
        const joystickMarginBottom = isPhone
            ? PROCORE_BOTTOM_ENTRY.TOOLBAR_HEIGHT + TOOLBAR_LAYOUT.MARGIN_MOBILE + JOYSTICKS_LAYOUT.MARGIN_BOTTOM_MOBILE
            : PROCORE_BOTTOM_ENTRY.TOOLBAR_HEIGHT +
              TOOLBAR_LAYOUT.MARGIN_TABLET +
              JOYSTICKS_LAYOUT.MARGIN_BOTTOM_TABLET_LANDSCAPE;

        const cornerHeight = joystickMarginBottom + JOYSTICKS_LAYOUT.HEIGHT + p;

        bottomLeft = {
            width: margin + JOYSTICKS_LAYOUT.QUAD_WIDTH + (orientation === "landscape" ? 88 : 76) + p,
            height: cornerHeight,
        };
        // Right joystick is narrower; add some extra for the orientation switch button.
        bottomRight = {
            width: margin + JOYSTICKS_LAYOUT.QUAD_WIDTH + (orientation === "landscape" ? 60 : 0) + p,
            height: cornerHeight,
        };

        if (orientation === "portrait") {
            bottom += 112;
        } else {
            bottom -= 80;
        }
    }

    return { top, right, bottom, left, topLeft, topRight, bottomLeft, bottomRight };
}
